<script lang="ts">
    import {onMount} from "svelte";
    import {unitQualifications} from "$lib/calls/qualification";
    import {getTariffs} from "$lib/calls/tariff";
    import type {ContractHistory, Employee} from "$lib/types/employee";
    import {page} from "$app/stores";
    import {Button, Checkbox, Dropdown, DropdownItem, Heading} from "flowbite-svelte";
    import dayjs from "dayjs";
    import {asValue} from "$lib/utils/lenkradDate";
    import SortableTable from "$lib/components/SortableTable.svelte";
    import ContractActions from "$lib/partials/employee/ContractActions.svelte";
    import ContractStatusCard from "$lib/partials/employee/ContractStatusCard.svelte";
    import {contractHeader, elseHeader, ptoHeader, tariffHeader} from "$lib/utils/tableHeaders/employeeContract";
    import {getDocumentTemplates} from "$lib/calls/settings";
    import CardLayout from "$items/CardLayout.svelte";
    import {listView} from "$lib/stores/viewStore";
    import {qualifications} from "$lib/stores/qualifications";
    import {tariffs} from "$lib/stores/tariffs";
    import {templateDocuments} from "$lib/stores/templateDocuments";
    import EmployeeContract from "$lib/partials/employee/EmployeeContract.svelte";
    import {getCostCenters} from "$lib/calls/unit";
    import {costCenters} from "$lib/stores/costCenters";

    let employee: Employee = $page.data.employee;


    let listedHistory = employee.contracts.map(x => x.history.map(h => ({
        ...h,
        terminated: x.history.find(y => y.hiredAs.includes('(aufgelöst)'))?.endDate
    }))).flat()

    let localQualifications = [];


    let localTariffs = [];
    let templates = [];


    $: contracts = employee.contracts.sort((a, b) => b.id - a.id);

    let localHeader = {
        contractHeader: [],
        tariffHeader: []
    }
    let filteredHeaderItems = {
        contractHeader:[],
        tariffHeader:[]
    }

    let color = 'text-neutral-600'

    const parseTimeframe = (contract: EmployeeContract):string => {
        if(!contract.currentHistory || typeof contract.currentHistory.startDate === 'undefined') {
            return '';
        }
        const from = dayjs(asValue(contract.completeTimeframe.startDate)).format('DD.MM.YYYY');
        const modifiedHistory = contract.history.find((x:ContractHistory) => x.historyType === 'document' && x.hiredAs.includes('(aufgelöst)'));
        const to = modifiedHistory ? dayjs(asValue(modifiedHistory.endDate)).format('DD.MM.YYYY') + ' <span class="text-red-600 line-through">(' + dayjs(asValue(contract.currentHistory.endDate)).format('DD.MM.YYYY')+')</span>' : (!asValue(contract.completeTimeframe.endDate) ? 'unbefristet' : dayjs(asValue(contract.currentHistory.endDate)).format('DD.MM.YYYY')) ;

        color = 'text-neutral-600'
        if(dayjs().isAfter(modifiedHistory ? dayjs(asValue(modifiedHistory.endDate)) : dayjs(asValue(contract.currentHistory.endDate)))) {

            color = 'text-neutral-400'

        } else if(contract.currentHistory.employmentContractStatus !== 'synchronized'){
            color = 'text-primary-500'
        }
        return `<span class="${color}">` + from + ' - ' + to + `</span> | <span class="${color}"> ${contract.currentHistory.group} </span> | <span class="${color}"> ${localQualifications.find(x => x.id === contract.currentHistory.qualificationId)?.title || contract.currentHistory.hiredAs}</span>`


    }

    const exchangeHeaderItems = (ev: Event, item: {label: string}, type: 'contractHeader' | 'tariffHeader') => {
        if(ev.target.checked) {
            // move to localHeader
            localHeader[type] = [...localHeader[type], item]
            filteredHeaderItems[type] = filteredHeaderItems[type].filter(x => x.label !== item.label)
        } else {
            localHeader[type] = localHeader[type].filter(x => x.label !== item.label)
            filteredHeaderItems[type] = [...filteredHeaderItems[type], item]
        }
    }


    onMount(async () => {
        localQualifications = $qualifications.find(x => x.unitId === employee.unitId)?.qualifications || [];
        if (localQualifications.length === 0) {
            localQualifications = await unitQualifications(employee.unitId);
            $qualifications = [...$qualifications, ...localQualifications];
            console.log('no qualifications')
        }
        localTariffs = $tariffs
        if ($tariffs.length === 0) {
            localTariffs = await getTariffs()
            $tariffs = localTariffs;
        }
        templates = $templateDocuments
        if ($templateDocuments.length === 0) {
            templates = await getDocumentTemplates()
            $templateDocuments = templates
        }
        if($costCenters.length === 0) {
            $costCenters = await getCostCenters()
        }
        localHeader = {
            contractHeader: contractHeader(employee.contracts, employee, $costCenters),
            tariffHeader: tariffHeader(localQualifications, localTariffs)
        }
        filteredHeaderItems.tariffHeader = localHeader.tariffHeader.filter(x => x.label === 'Tarif' || x.label === 'Feiertagszuschlag')
        filteredHeaderItems.contractHeader = [localHeader.contractHeader.find(x => x.label === 'Gruppe')]
        localHeader.contractHeader = localHeader.contractHeader.filter(x => x.label !== 'Gruppe')
        localHeader.tariffHeader = localHeader.tariffHeader.filter(x => x.label !== 'Tarif' && x.label !== 'Feiertagszuschlag')

    })
</script>

<div>
    {#if $listView}
        <div>
            <div class="flex justify-between items-center">
                <Heading class="text-neutral-600 mb-3" tag="h4">Tarif</Heading>
                <div>
                    <Button size="sm">Felder</Button>
                    <Dropdown >
                        {#each [...localHeader.tariffHeader, ...filteredHeaderItems.tariffHeader].sort((a, b) => a.label.localeCompare(b.label)) as header}
                            <DropdownItem>
                                <Checkbox on:change={(ev) => exchangeHeaderItems(ev, header, 'tariffHeader') } checked={filteredHeaderItems.tariffHeader.find(x => x.label === header.label) === undefined}>{header.label}</Checkbox>
                            </DropdownItem>
                        {/each}
                    </Dropdown>
                </div>
            </div>
            <SortableTable header={localHeader.tariffHeader}
                           data={listedHistory.filter(x => x.historyType !== 'document')}></SortableTable>
            <div class="flex justify-between items-center">
                <Heading class="text-neutral-600 my-3" tag="h4">Vertrag</Heading>
                <div>
                    <Button size="sm">Felder</Button>
                    <Dropdown >
                        {#each [...localHeader.contractHeader, ...filteredHeaderItems.contractHeader].sort((a, b) => a.label.localeCompare(b.label)) as header}
                            <DropdownItem>
                                <Checkbox on:change={(ev) => exchangeHeaderItems(ev, header, 'contractHeader') } checked={filteredHeaderItems.contractHeader.find(x => x.label === header.label) === undefined}>{header.label}</Checkbox>
                            </DropdownItem>
                        {/each}
                    </Dropdown>
                </div>
            </div>
            <SortableTable header={localHeader.contractHeader}
                           data={listedHistory.filter(x => x.historyType !== 'document')}></SortableTable>
            <Heading class="text-neutral-600 my-3" tag="h4">Urlaub</Heading>
            <SortableTable header={ptoHeader}
                           data={listedHistory.filter(x => x.historyType !== 'document')}></SortableTable>
            <Heading class="text-neutral-600 my-3" tag="h4">Sonstiges</Heading>
            <SortableTable header={elseHeader}
                           data={listedHistory.filter(x => x.historyType === 'document')}></SortableTable>
        </div>
    {:else}
        <div class="space-y-2">
            {#each contracts as contract, i}
                {@const history = contract.history.sort((a,b) => b.id - a.id).map((x) => ({...x, terminated: contract.history.find(y => y.hiredAs.includes('(aufgelöst)'))?.endDate}))}
                {@const title = localQualifications.find(x => x.id === contract.currentHistory.qualificationId)?.title || contract.currentHistory.hiredAs}
                <CardLayout title='' accordion display={i === 0}>
                    <svelte:fragment slot="card-header">
                        <div class="-m-6 w-full flex gap-8 text-xl items-center ">
                            {@html parseTimeframe(contract)}

                        </div>

                    </svelte:fragment>
                    <div>
                        <div class="px-4 py-3 mb-5 space-y-2">

                            <ContractStatusCard {employee} {contract}/>
                            <div class="flex  gap-3 ">
                                <div class="flex-1 max-w-[75vw] ">
                                    <div class="flex justify-between items-center">
                                        <Heading class="text-neutral-600 mb-3" tag="h4">Tarif</Heading>
                                        <div>
                                            <Button size="sm">Felder</Button>
                                            <Dropdown >
                                                {#each [...localHeader.tariffHeader, ...filteredHeaderItems.tariffHeader].sort((a, b) => a.label.localeCompare(b.label)) as header}
                                                    <DropdownItem>
                                                        <Checkbox on:change={(ev) => exchangeHeaderItems(ev, header, 'tariffHeader') } checked={filteredHeaderItems.tariffHeader.find(x => x.label === header.label) === undefined}>{header.label}</Checkbox>
                                                    </DropdownItem>
                                                {/each}
                                            </Dropdown>
                                        </div>
                                    </div>
                                    <SortableTable header={localHeader.tariffHeader}
                                                   data={history.filter(x => x.historyType !== 'document')}></SortableTable>
                                    <div class="flex justify-between items-center">
                                        <Heading class="text-neutral-600 my-3" tag="h4">Vertrag</Heading>
                                        <div>
                                            <Button size="sm">Felder</Button>
                                            <Dropdown >
                                                {#each [...localHeader.contractHeader, ...filteredHeaderItems.contractHeader].sort((a, b) => a.label.localeCompare(b.label)) as header}
                                                    <DropdownItem>
                                                        <Checkbox on:change={(ev) => exchangeHeaderItems(ev, header, 'contractHeader') } checked={filteredHeaderItems.contractHeader.find(x => x.label === header.label) === undefined}>{header.label}</Checkbox>
                                                    </DropdownItem>
                                                {/each}
                                            </Dropdown>
                                        </div>
                                    </div>
                                    <SortableTable header={localHeader.contractHeader}
                                                   data={history.filter(x => x.historyType !== 'document')}></SortableTable>
                                    <Heading class="text-neutral-600 my-3" tag="h4">Urlaub</Heading>
                                    <SortableTable header={ptoHeader}
                                                   data={history.filter(x => x.historyType !== 'document')}></SortableTable>
                                    <Heading class="text-neutral-600 my-3" tag="h4">Sonstiges</Heading>
                                    <SortableTable header={elseHeader}
                                                   data={history.filter(x => x.historyType === 'document')}></SortableTable>
                                </div>
                                <div class="w-44 border-l border-neutral-200 pl-3">
                                    <ContractActions bind:employee {contract}
                                                     currentContract={employee.currentContract}></ContractActions>

                                </div>
                            </div>
                        </div>
                    </div>
                </CardLayout>
            {/each}
        </div>
    {/if}
</div>

